import noop from 'lodash/noop'
import moment from 'moment'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Moment from 'react-moment'
import styled from 'styled-components'
import {
  Inline,
  Text,
  Box,
  IconInfoInverted,
  TooltipRenderer,
  Stack
} from '@machobear/design-system'
import { TextButton } from '@/components/button'
import { ClockIcon, DoubleCheckmark, ReadCheckmark } from '@/components/icons'
import { breakpoints, colors } from '@/styles'
import { snakeToSentence } from '@/pages/Settings/AutoReply/helpers/utils'
import useRole from '@/hooks/use-role'
import { translateMessage, translateSupportMessage } from '../../ports'

const Wrapper = styled.div`
  display: flex;
  flex-direction: ${props => (props.isThem ? 'row' : 'row-reverse')};
`

const TimeStampWrapper = styled.div`
  display: flex;
  flex-direction: ${props => (props.isThem ? 'row' : 'row-reverse')};
  align-self: ${props =>
    props.isSystemMessage ? 'center' : props.isThem ? 'flex-start' : 'flex-end'};
  justify-content: space-between;
  width: ${props => (props.isSystemMessage ? 'unset' : '100%')};
  margin: ${props => (props.isSystemMessage ? '0' : '8px 0 15px')};
  font-size: 11px;
  letter-spacing: -0.2px;
  min-height: 21px;
  color: ${colors.mediumGrey};
  flex-shrink: 0;

  ${props =>
    props.onlyTranslate &&
    `
    margin: 1px 0 0;
    min-height: unset;
  `}
  margin-left: 0;

  ${props =>
    !props.groupChatNotLastMessage &&
    `
    margin-left: 32px;
  `}

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    align-self: ${props => (props.isThem || props.isSystemMessage ? 'flex-start' : 'flex-end')};
  }
`

const TranslateButton = styled(TextButton)`
  font-size: 11px;
  outline: none;
  margin-right: 4px;
  ${props =>
    props.isThem &&
    `
    margin-right: 0;
    margin-left: 4px;
  `}

  ${props =>
    props.isTranslated &&
    `
    font-style: italic;
    color: ${colors.mediumGrey};
    text-decoration: underline;
    cursor: default;
    :hover {
      opacity: 1;
    }
  `}
`

const ReceiptWrapper = styled.div`
  display: flex;
  ${props =>
    !props.isThem &&
    `
    cursor: pointer;
    width: 100%;
  `}
`

const ReceiptTextContainer = styled.div`
  display: flex;
`
const ReceiptText = styled.span`
  margin-right: 5px;
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 100%;

  ${props =>
    props.isAutomatic &&
    `
    justify-content: space-between;
  `}
`

const AutomatedText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-right: 5px;
`

const StyledDoubleCheckmark = styled(DoubleCheckmark)`
  margin-left: 6px;
`
const StyledReadCheckmark = styled(ReadCheckmark)`
  margin-left: 6px;
`

const Timestamp = ({
  groupChatNotLastMessage,
  showTime,
  isThem,
  event,
  isRead,
  isSystemMessage = false,
  setTranslatedText = noop,
  translatedText,
  showDrawer,
  showIcons = false
}) => {
  const { i18n, t } = useTranslation()
  const isLandlord = useRole() === 'landlord'

  const currentLanguage = i18n.languages[0]
  useEffect(() => {
    setTranslatedText(undefined)
  }, [currentLanguage, setTranslatedText])

  const handleTranslate = async () => {
    const fn = isSupport ? translateSupportMessage : translateMessage
    const { body, response } = await fn(
      { body: { id: event.id, message: event?.event_info?.message } },
      { language: currentLanguage }
    )
    if (response.ok) {
      setTranslatedText(body[event.id]?.translated_text)
    }
  }

  const isSupport = event?.event_type === 'support_room_event_chat'

  const isChat = event?.event_type.match(/^(chat|support_room_event_chat|chat_initial_message)$/)
  const shouldTranslate =
    !(currentLanguage === 'en' && event.detected_lang === 'latin') && !!event.detected_lang

  const timestampYear = moment(event?.created_at).year()
  const calendarStrings = {
    sameDay: `[${t('b.chat.view.today.text', 'Today')}], h:mm A`,
    lastDay: 'ddd, h:mm A',
    lastWeek: 'MMM DD, h:mm A',
    sameElse: now => {
      return timestampYear === now.year() ? 'MMM DD, h:mm A' : 'MMM DD, YYYY, h:mm A'
    }
  }

  const handleTimeStampClick = () => {
    if (!isThem) showDrawer()
  }

  if (!showTime && !shouldTranslate) return null

  return (
    <TimeStampWrapper
      groupChatNotLastMessage={groupChatNotLastMessage}
      isThem={isThem}
      isSystemMessage={isSystemMessage}
      onlyTranslate={!showTime && shouldTranslate && isChat}
    >
      {event.pending ? (
        <ReceiptTextContainer>
          <ReceiptText>{t('b.chat.view.sending.text', 'Sending...')}</ReceiptText>
          <ClockIcon />
        </ReceiptTextContainer>
      ) : (
        <>
          <ReceiptWrapper isThem={isThem} onClick={handleTimeStampClick}>
            {showTime && (
              <>
                <InfoWrapper isAutomatic={event.event_type === 'chat_auto_reply'}>
                  {event.event_type === 'chat_auto_reply' && isLandlord && (
                    <AutomatedText>
                      <em>
                        {t('l.settings.view.auto_reply.automated_message', 'Automated message')}
                      </em>
                      <TooltipRenderer
                        tooltip={
                          <Stack space={'medium'}>
                            <Text size="small">
                              {t(
                                'l.settings.view.auto_reply.automated_message_tooltip',
                                'Only you and other managing landlords can see that this is an automated message.'
                              )}
                            </Text>
                            <Inline space="xxsmall">
                              <Text size="small">
                                {t('l.settings.view.auto_reply.created_by', '• Created by:')}
                              </Text>
                              <Text size="small">{event.display_name}</Text>
                            </Inline>
                            <Inline space="xxsmall">
                              <Text size="small">
                                {t('l.settings.view.auto_reply.trigger_name', '• Trigger Name:')}
                              </Text>
                              <Text size="small">
                                {snakeToSentence(event.event_info.auto_reply_trigger_txt_id)}
                              </Text>
                            </Inline>
                          </Stack>
                        }
                      >
                        {({ triggerProps }) => (
                          <Box
                            {...triggerProps}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <IconInfoInverted size="16px" tone="#7A7A7A" />
                          </Box>
                        )}
                      </TooltipRenderer>
                    </AutomatedText>
                  )}
                  <Box display="flex">
                    <Moment calendar={calendarStrings} local>{`${event.created_at}-0000`}</Moment>
                    {!isThem &&
                      !isSupport &&
                      showIcons &&
                      (isRead ? <StyledReadCheckmark /> : <StyledDoubleCheckmark />)}
                  </Box>
                </InfoWrapper>
              </>
            )}
          </ReceiptWrapper>
          <Wrapper isThem={isThem}>
            {shouldTranslate && isChat && (
              <TranslateButton
                isThem={isThem}
                isTranslated={!!translatedText}
                onClick={handleTranslate}
              >
                {!!translatedText
                  ? t('b.chat.chat_event.translated.text', 'Translated')
                  : t('b.chat.chat_event.translate.button', 'Translate')}
              </TranslateButton>
            )}
          </Wrapper>
        </>
      )}
    </TimeStampWrapper>
  )
}

export default Timestamp
